import {
  Modal,
  TextField,
  Button,
  Popover,
  ActionList,
  Box,
} from "@medentic/polaris-react";
import { useCallback, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";

export type NoteTone = "default" | "attention" | "warning" | "critical";

interface NotesModalProps {
  open: boolean;
  onClose: () => void;
  notes: string;
  tone: NoteTone;
  onNotesChange: (value: string) => void;
  onToneChange: (value: NoteTone) => void;
  onSave: () => void;
  toneOptions: Array<{
    value: NoteTone;
    label: string;
    description: string;
  }>;
  isSaving: boolean;
  i18nKeyPrefix?: string;
}

function NotesModal({
  open,
  onClose,
  notes,
  tone,
  onNotesChange,
  onToneChange,
  onSave,
  toneOptions,
  isSaving,
  i18nKeyPrefix = "patientDetails",
}: NotesModalProps) {
  const { t } = useTranslation();
  const [popoverActive, setPopoverActive] = useState(false);

  const togglePopoverActive = useCallback(
    () => setPopoverActive((popoverActive) => !popoverActive),
    []
  );

  const activator = useMemo(
    () => (
      <Button onClick={togglePopoverActive} disclosure>
        {toneOptions.find((option) => option.value === tone)?.label}
      </Button>
    ),
    [togglePopoverActive, toneOptions, tone]
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={
        notes
          ? t(`${i18nKeyPrefix}.notes.modal.editTitle`)
          : t(`${i18nKeyPrefix}.notes.modal.addTitle`)
      }
      primaryAction={{
        content: t("common.save"),
        onAction: onSave,
        loading: isSaving,
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: onClose,
          disabled: isSaving,
        },
      ]}
      onTransitionEnd={() => {
        document.body.setAttribute("data-lock-scrolling-hidden", "true");
      }}
    >
      <Modal.Section>
        <TextField
          label={""}
          value={notes}
          onChange={onNotesChange}
          multiline={4}
          autoComplete="off"
        />
        <Box paddingBlockStart="400">
          <Popover
            active={popoverActive}
            activator={activator}
            autofocusTarget="first-node"
            onClose={togglePopoverActive}
            preferredPosition="below"
            preferredAlignment="left"
          >
            <ActionList
              actionRole="menuitem"
              sections={[
                {
                  items: toneOptions.map((option) => ({
                    content: option.label,
                    helpText: option.description,
                    onAction: () => {
                      onToneChange(option.value);
                      setPopoverActive(false);
                    },
                  })),
                },
              ]}
            />
          </Popover>
        </Box>
      </Modal.Section>
    </Modal>
  );
}

export default NotesModal;
